import type { ReactNode } from 'react';
import { DecoratorNode, Spread, SerializedLexicalNode } from 'lexical';
import { PAGE_GAP_PX } from '../pagination/constants';

export type SerializedSoftPageBreakNode = Spread<{
  type: 'soft-page-break';
  version: 1;
  heightPx: number;
}, SerializedLexicalNode>;

function SoftBreakComponent({ heightPx }: { heightPx: number }) {
  return (
    <div
      aria-hidden
      className="soft-page-break"
      style={{ height: `${Math.max(0, Math.floor(heightPx))}px` }}
    />
  );
}

export class SoftPageBreakNode extends DecoratorNode<ReactNode> {
  __heightPx: number;

  static getType(): string { return 'soft-page-break'; }
  static clone(node: SoftPageBreakNode): SoftPageBreakNode { return new SoftPageBreakNode(node.__heightPx, node.__key); }

  constructor(heightPx: number = PAGE_GAP_PX, key?: string) {
    super(key);
    this.__heightPx = heightPx;
  }

  exportJSON(): SerializedSoftPageBreakNode {
    return { type: 'soft-page-break', version: 1, heightPx: this.__heightPx };
  }

  static importJSON(json: SerializedSoftPageBreakNode): SoftPageBreakNode {
    return new SoftPageBreakNode(json.heightPx);
  }

  createDOM(): HTMLElement {
    const el = document.createElement('div');
    el.className = 'soft-page-break';
    el.setAttribute('data-soft-page-break', '1');
    el.setAttribute('contenteditable', 'false');
    el.style.height = `${Math.max(0, Math.floor(this.__heightPx))}px`;
    return el;
  }

  updateDOM(_prevNode: SoftPageBreakNode, dom: HTMLElement): boolean {
    dom.style.height = `${Math.max(0, Math.floor(this.__heightPx))}px`;
    return false;
  }

  decorate(): ReactNode {
    return <SoftBreakComponent heightPx={this.__heightPx} />;
  }
}

export function $createSoftPageBreakNode(heightPx?: number): SoftPageBreakNode {
  return new SoftPageBreakNode(heightPx ?? PAGE_GAP_PX);
}

export function $isSoftPageBreakNode(node: any): node is SoftPageBreakNode {
  return node instanceof SoftPageBreakNode;
}
